/** @typedef {import('./SubscriptionCard/Props').SharedProps} Props */

import { subscriptionService } from '../../service-container';
import { translate } from '../../App/Internationalization';
import captureError from '../../utils/captureError';
import ExternalAnchorButton from '../ExternalAnchorButton';
import FormGrayMessages from '../Forms/Messages/FormGrayMessages';
import FormSuccessMessages from '../Forms/Messages/FormSuccessMessages';
import Loading from '../Loading';
import React from 'react';

const btnClass = 'button w-full p-3 text-ms-3';

/**
 * @param {Props} props
 * @returns	{JSX.Element}
 */

// eslint-disable-next-line complexity
function SubscribeButton(props) {
	const [isCallingApi, setIsCallingApi] = React.useState(false);
	const [errorMessage, setErrorMessage] = React.useState(
		/** @type {string | null} */ (null)
	);
	const [successMessage, setSuccessMessage] = React.useState(
		/** @type {string | null} */ (null)
	);

	if (props.isGettingData || props.subscription === null || isCallingApi) {
		return (
			<div className={`${btnClass} pointer-events-none`}>
				<div className="-my-19 -scale-50">
					<Loading />
				</div>
			</div>
		);
	}

	const currentSubscription = props.subscription;
	const tier = props.tier.title;
	const targetTier = props.tier.tier;
	const url = props.paymentLinks?.[tier]?.url;
	const isGroupSubscription = props.checkoutRole === 'manager';
	const hasExistingSubscription = props.hasExistingSubscription ?? false;
	const localPlanChangeStatus = props.lastPlanChange?.status;
	const hasMatchedTierFromState =
		props.lastPlanChange?.targetTier === targetTier;

	// Determine if this is the current plan
	const isCurrentTier =
		(isGroupSubscription ? hasExistingSubscription : true) &&
		((currentSubscription.isActive &&
			currentSubscription.priceInCents === props.tier.pricePerMonth) ||
			(!currentSubscription.isActive && props.tier.pricePerMonth === 0));

	// Determine label from local plan-change state after a successful API update.
	let buttonText = translate('subscription.plans.cta'); // Default "Subscribe" or similar
	const feedbackMessages = (
		<React.Fragment>
			<FormSuccessMessages
				messages={successMessage ? [successMessage] : null}
			/>
			<FormGrayMessages
				heading="info"
				messages={errorMessage ? [errorMessage] : null}
			/>
		</React.Fragment>
	);

	if (isCurrentTier) {
		if (
			hasMatchedTierFromState &&
			(localPlanChangeStatus === 'upgraded' ||
				localPlanChangeStatus === 'downgraded')
		) {
			buttonText = translate(
				`subscription.plans.${localPlanChangeStatus}`
			);
		} else {
			buttonText = translate('subscription.plans.current_plan'); // e.g. "Current Plan"
		}
	} else {
		// If they have an active plan and this isn't it, it's either an upgrade or downgrade
		const currentPrice = currentSubscription.priceInCents;
		const tierPrice = props.tier.pricePerMonth;
		if (tierPrice > currentPrice) {
			buttonText = translate('subscription.plans.upgrade'); // e.g. "Downgrade"
		} else if (tierPrice < currentPrice) {
			buttonText = translate('subscription.plans.downgrade'); // e.g. "Upgrade"
		}
	}

	if (isCurrentTier) {
		return (
			<React.Fragment>
				<button
					className={`${btnClass} !bg-theme-gray !text-theme-fg-light cursor-default`}
					disabled={true}
					type="button"
				>
					{buttonText}
				</button>
				{feedbackMessages}
			</React.Fragment>
		);
	}

	// eslint-disable-next-line complexity
	async function handlePaidSubscriptionClick() {
		if (targetTier === undefined || isCallingApi) {
			return;
		}

		setIsCallingApi(true);
		setErrorMessage(null);
		setSuccessMessage(null);

		try {
			if (
				isGroupSubscription &&
				hasExistingSubscription &&
				!props.groupId
			) {
				throw new Error(translate('global.error'));
			}

			const apiMessage =
				isGroupSubscription && hasExistingSubscription
					? await subscriptionService.callGroupSubscriptionAction(
							props.groupId,
							targetTier
					  )
					: await subscriptionService.callSubscriptionAction(
							targetTier
					  );
			const status =
				props.tier.pricePerMonth > currentSubscription.priceInCents
					? 'upgraded'
					: 'downgraded';
			setSuccessMessage(
				apiMessage ?? translate(`subscription.plans.${status}`)
			);
			props.onPlanChanged?.({
				priceInCents: props.tier.pricePerMonth,
				status,
				targetTier,
			});
		} catch (error) {
			let message = translate('global.error');

			if (error instanceof Error && error.message) {
				message = error.message;
			}

			setErrorMessage(message);
			captureError(error);
		} finally {
			setIsCallingApi(false);
		}
	}

	if (
		(isGroupSubscription && !hasExistingSubscription) ||
		(!isGroupSubscription && currentSubscription.priceInCents === 0)
	) {
		return (
			<ExternalAnchorButton
				className={btnClass}
				href={url}
				target="_self"
			>
				{buttonText}
			</ExternalAnchorButton>
		);
	}

	return (
		<React.Fragment>
			<button
				className={`${btnClass} !bg-theme-gray !text-theme-fg-light cursor-pointer`}
				disabled={isCallingApi}
				onClick={handlePaidSubscriptionClick}
				type="button"
			>
				{buttonText}
			</button>
			{feedbackMessages}
		</React.Fragment>
	);
}

export default React.memo(SubscribeButton);
